import React, { useEffect } from "react";
import { bool } from "prop-types";
import { OptAnonContent } from "./OptAnonContent";
import { getConfig } from "../utils/runtimeConfig";

/**
 * Conditionally adds the OneTrust CDN script to the document
 * @param {boolean} cookieManagementEnabled - defines if CDN will be added
 * @returns {JSX.Element} a div wrapper containing the children passed
 * @example
 *   <OneTrust
 *     cookieManagementEnabled={company.cookieManagementEnabled}
 *   >
 *     <Children/>
 *   </OneTrust>
 * @type {{( props: { cookieManagementEnabled: boolean }): JSX.Element}}
 */
export const OneTrust = ({ cookieManagementEnabled, children }) => {
  useEffect(() => {
    // Runtime config (window.ENV via env.js, overridden per-env by a ConfigMap),
    // not the build-time process.env value — so one image works across
    // environments. AB#645437.
    const scriptKey = getConfig("REACT_APP_ONETRUST_DATA_DOMAIN_SCRIPT_KEY");
    // OneTrust can (re)publish the consent script with or without an
    // OtAutoBlock.js auto-blocking companion, independently per environment,
    // so whether to inject it is driven by config rather than inferred.
    const autoBlockEnabled =
      getConfig("REACT_APP_ONETRUST_AUTOBLOCK_ENABLED") === "true";

    const newScriptOne = document.createElement("script");
    newScriptOne.async = true;
    newScriptOne.src = "https://cdn.cookielaw.org/scripttemplates/otSDKStub.js";
    newScriptOne.type = "text/javascript";
    newScriptOne.id = "OneTrustCDN";
    newScriptOne.setAttribute("data-domain-script", scriptKey);

    const newScriptTwo = document.createElement("script");
    newScriptTwo.async = true;
    newScriptTwo.id = "OneTrustWrapper";
    newScriptTwo.innerHTML = `function OptanonWrapper() {${OptAnonContent}}`;

    if (cookieManagementEnabled && !document.getElementById("OneTrustCDN")) {
      const metaEl = document.getElementsByTagName("meta")[0];
      metaEl.parentNode.insertBefore(newScriptTwo, metaEl);
      metaEl.parentNode.insertBefore(newScriptOne, newScriptTwo);
      // Inject in OneTrust's documented order: OtAutoBlock (when enabled)
      // ahead of otSDKStub, then the OptanonWrapper definition.
      if (autoBlockEnabled && scriptKey) {
        const autoBlockScript = document.createElement("script");
        autoBlockScript.type = "text/javascript";
        autoBlockScript.id = "OneTrustAutoBlock";
        autoBlockScript.src = `https://cdn.cookielaw.org/consent/${scriptKey}/OtAutoBlock.js`;
        metaEl.parentNode.insertBefore(autoBlockScript, newScriptOne);
      }
    } else if (
      !cookieManagementEnabled &&
      document.getElementById("OneTrustCDN")
    ) {
      ["OneTrustAutoBlock", "OneTrustCDN", "OneTrustWrapper"].forEach(id => {
        const el = document.getElementById(id);
        if (el && el.parentNode) {
          el.parentNode.removeChild(el);
        }
      });
    }
  }, [cookieManagementEnabled]);

  return <div data-name="OneTrust">{children}</div>;
};

OneTrust.propTypes = {
  cookieManagementEnabled: bool.isRequired
};

export default OneTrust;
